import React, { useCallback, useEffect, useState } from 'react';
import css from './styles.module.scss';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { selectNewDeal, selectIsDealEditing } from '~/features/dashboard/store/selectors';
import { Controller, useForm } from 'react-hook-form';
import { StepControls } from '~/common/step-controls';
import { INewDealStepSevenFormInputs } from '~/models/deals/new-deal/formInputs';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { createNewDealStepSevenThunk, getDealTypes } from '~/features/dashboard/store/actions.thunks';
import * as CreatorsListFeature from '~/features/creators-list';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import DoneIcon from '@material-ui/icons/Done';
import moment from 'moment-timezone';
import { TextFieldOutlined } from '~/common/text-field-outlined';
import classNames from 'classnames';
import MomentUtilsNext from '@material-ui/pickers-next/adapter/moment';
import { DesktopDatePicker, LocalizationProvider } from '@material-ui/pickers-next';
import { Moment } from 'moment';
import { useDateFormat } from '~/common/date-format';

interface Step_7Props {
    prevStep: () => void;
}

export const Step_7: React.FC<Step_7Props> = ({ prevStep }) => {
    const { selectedDateFormat } = useDateFormat();
    const newDealState = useSelector(selectNewDeal);
    const dispatch = useThunkDispatch();
    const [isDealComplete, setIsDealComplete] = useState<Boolean>(false);
    const route = useAppRoute();

    const {
        handleSubmit,
        errors,
        control,
        register,
        setError,
        clearErrors,
        getValues,
    } = useForm<INewDealStepSevenFormInputs>({
        defaultValues: {
            signatureDate: newDealState?.signatureDate ? newDealState?.signatureDate : '',
        },
    });

    const onSubmit = useCallback(
        (data: INewDealStepSevenFormInputs) => {
            dispatch(createNewDealStepSevenThunk({ ...data, isDealComplete: !isDealComplete }));
            setIsDealComplete(!isDealComplete);
        },
        [dispatch, isDealComplete]
    );

    useEffect(() => {
        dispatch(getDealTypes()).then(data => {
            if (data && data.error) {
                route(CreatorsListFeature.FEATURE_ROUTER_PATH);
            }
        });
    }, [dispatch, route]);

    const onStep7Btn = async () => {
        await dispatch(createNewDealStepSevenThunk({ isDealComplete: !isDealComplete, signatureDate: getValues('signatureDate') }));
        await setIsDealComplete(!isDealComplete);
    };

    useEffect(() => {
        setIsDealComplete(newDealState?.contractType === 'MAIN ARTIST' ? true : false);
    }, [newDealState]);

    const selectDealEditing = useSelector(selectIsDealEditing);

    return (
        <Box className={css.module}>
            <Box className={css.wrapper}>
                <Paper square elevation={0} className={classNames(css.paper, css.header)}>
                    <Typography align={'left'} variant={'h5'}>
                        {newDealState?.contractTitle}
                    </Typography>
                </Paper>
                <div className={css.stepsevenouter}>
                    <form className={css.form} onSubmit={handleSubmit(onSubmit)}>
                        {selectDealEditing && (
                            <LocalizationProvider dateAdapter={MomentUtilsNext}>
                                <div className={css.customdatepicker}>
                                    <Controller
                                        render={(controllerProps) => (
                                            <DesktopDatePicker
                                                onChange={newValue => {
                                                    clearErrors('signatureDate');
                                                    if (!newValue) {
                                                        controllerProps.onChange(null);
                                                    } else if (newValue.isValid()) {
                                                        controllerProps.onChange((newValue as Moment).format("MM/DD/YYYY") || null);
                                                    } else {
                                                        setError('signatureDate', { message: 'Invalid date' });
                                                    }
                                                }}
                                                value={controllerProps.value}
                                                inputFormat={selectedDateFormat}
                                                // inputFormat="MM/DD/YYYY"
                                                minDate={newDealState?.startDate ? moment(newDealState?.startDate) : undefined}
                                                maxDate={newDealState?.endDate ? moment(newDealState?.endDate) : undefined}
                                                renderInput={(props: any) => (
                                                    <TextFieldOutlined
                                                        {...props}
                                                        error={!!errors?.signatureDate}
                                                        helperText={(errors.signatureDate?.message) || ' '}
                                                        label={'Signature Date'}
                                                        name={'signatureDate'}
                                                        // defaultValue={newDealState?.signatureDate ? newDealState?.signatureDate : 'null'}
                                                    />
                                                )}
                                            />
                                        )}
                                        inputRef={register}
                                        name={'signatureDate'}
                                        control={control}
                                    />
                                </div>
                            </LocalizationProvider>
                        )}
                    </form>
                    <StepControls
                        className={css.stepControls}
                        onPrevClick={prevStep}
                        errors={errors}
                        nextButton={(
                            <Button variant="contained" color="primary" disableElevation startIcon={<DoneIcon />} onClick={() => onStep7Btn()}>
                                Mark deal as complete
                            </Button>
                        )}
                    />
                </div>
            </Box>
        </Box>
    );
};
